// this could be defined in the page schema or something eventually?

const AB_TEST_DATA_LAYER_INIT_EVENT_NAME = 'test_ready';

/**
 * Push the event that starts the A/B test and runs the experiment snippet.
 */
export const startTestEvent = () => {
    if (window.dataLayer === undefined) {
        // eslint-disable-next-line
        console.warn('dataLayer not available');
        return;
    }
    window.dataLayer.push({
        event: AB_TEST_DATA_LAYER_INIT_EVENT_NAME
    });
};

/**
 * Push the data layer event, set the toggle and set the values from the experiment.
 * @param {Function} dispatch - The redux store dispatch function
 * @param {String} experimentId - The configuration for AB test experimentId eg - "energyMeterUsage"
 * @param {Array} experimentValues - The configuration for setting preload values via AB tests eg - ['data.whatToCompare', 'E']
 */
export const setupExperiment = (dispatch, experimentId, experimentValues) => {
    if (typeof window === 'undefined') {
        throw Error('Attempted to setup AB tests from O360 on the server - window is not defined!');
    }
    setExperimentId(dispatch, experimentId);
    setExperimentValues(dispatch, experimentValues);
};

/**
 * Set the experimentId from the experiment snippet to the app state at page.experimentId
 * example experimentId: "energyMeterUsage"
 * @param {Function} dispatch - The redux store dispatch function
 * @param {String} experimentId - The configuration for AB test experimentId eg - "energyMeterUsage"
 */
const setExperimentId = (dispatch, experimentId) => {
    if (experimentId) {
        dispatch({ type: 'SET_EXPERIMENT_ID', value: experimentId });
    } else if (process.env.EVEREST_DEPLOY_ENV?.trim() !== 'prod') {
        // eslint-disable-next-line no-console
        console.warn('experimentId is undefined - no experimentId will be added for this page');
    }
};

/**
 * Set the values from the experiment snippet to the app state.
 * example value config: [identifier, value] eg ['data.whatToCompare', 'E']
 * @param {Function} dispatch - The redux store dispatch function
 * @param {Array} experimentValues - The configuration for setting preload values via AB tests eg - ['data.whatToCompare', 'E']
 */
const setExperimentValues = (dispatch, experimentValues) => {
    if (experimentValues) {
        for (const valueAction of experimentValues) {
            const [identifier, value] = valueAction;
            dispatch({
                type: 'SET_VALUE',
                identifier,
                value,
                stateKey: 'vertical.quote'
            });
        }
    } else if (process.env.EVEREST_DEPLOY_ENV?.trim() !== 'prod') {
        // eslint-disable-next-line no-console
        console.warn(
            'experimentValues is undefined - no preload values will be added for this page from O360'
        );
    }
};

export const setupSplitTraffic = (dispatch, splitTrafficId) => {
    if (typeof window === 'undefined') {
        throw Error('Attempted to setup traffic split on the server - window is not defined!');
    }
    setSplitTrafficId(dispatch, splitTrafficId);
};

const setSplitTrafficId = (dispatch, splitTrafficId) => {
    if (splitTrafficId) {
        dispatch({ type: 'SET_SPLIT_TRAFFIC_ID', value: splitTrafficId });
    } else if (process.env.EVEREST_DEPLOY_ENV?.trim() !== 'prod') {
        // eslint-disable-next-line no-console
        console.warn('splitTrafficId is undefined - no splitTrafficId will be added for this page');
    }
};

const startCustomExperiment = (dispatch, experimentId, attr) => {
    if (experimentId) {
        setExperimentId(dispatch, experimentId);
        dispatch({ type: 'SET_EXPERIMENT_DATA', value: attr });
    } else if (process.env.EVEREST_DEPLOY_ENV?.trim() !== 'prod') {
        console.warn('experimentId is undefined - no experiment state will be saved');
    }
};

/**
 * Set a function on window that can be called from Google Optimise 360 (O360) to run the A/B test snippet
 * @param {Function} dispatch - The redux store dispatch function
 * @example
 * window.runExperiment && window.runExperiment(
     "energyMeterUsage", //  experimentId
     [
         ["data.whatToCompare", "E"], // prefill data that changes our redux store
         ["data.movingIn", "Y"]
     ]
    );
 */
export const setupExperimentRunner = (dispatch) => {
    window.runExperiment = (experimentId, experimentValues) => {
        setupExperiment(dispatch, experimentId, experimentValues);
    };
    window.splitTraffic = (splitTrafficId) => {
        setupSplitTraffic(dispatch, splitTrafficId);
    };
    window.startCustomExperiment = (experimentId, attr) => {
        startCustomExperiment(dispatch, experimentId, attr);
    };
    // Initiate the O360 A/B test snippet
    startTestEvent();
};
